import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { AlertCircle } from 'lucide-react';
import { PublicBlockRenderer } from './PublicBlockRenderer';
import { PublicHeader } from './PublicHeader';
import { PublicFooter } from './PublicFooter';
import { AdminBar } from './AdminBar';
import { CookieBanner } from '@/features/legal/components/CookieBanner';
import { CustomScriptsInjector } from './CustomScriptsInjector';
import { PublicSiteMeta } from './PublicSiteMeta';
import { GoogleAnalyticsInjector } from '@/features/analytics/components/GoogleAnalyticsInjector';
import { PublicViewTracker } from '@/features/analytics/components/PublicViewTracker';
import { getTemplateIdForContent, resolveAppearance, appearanceCssVars, type PublicBranding } from '../lib/brandingVars';
import { loadGoogleFont } from '@/features/themes/lib/appearance';
import { usePublicMenu } from '@/features/themes/lib/useSiteMenu';
import { useSiteChrome } from '../lib/useSiteChrome';
import { getPureHtmlContent } from '../lib/pureHtmlPage';
import type { Block } from '@/types/blocks';

interface SiteSeoSettings {
  meta_title?: string;
  meta_description?: string;
  og_image?: string;
}

function getContentSeo(value: unknown): SiteSeoSettings {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const record = value as Record<string, unknown>;
  return {
    meta_title: typeof record.meta_title === 'string' ? record.meta_title : '',
    meta_description: typeof record.meta_description === 'string' ? record.meta_description : '',
    og_image:
      typeof record.og_image === 'string'
        ? record.og_image
        : typeof record.image === 'string'
          ? record.image
          : '',
  };
}

function getSiteSeo(themeSettings: unknown): SiteSeoSettings {
  if (!themeSettings || typeof themeSettings !== 'object' || Array.isArray(themeSettings)) return {};
  const seo = (themeSettings as { seo?: unknown }).seo;
  if (!seo || typeof seo !== 'object' || Array.isArray(seo)) return {};
  return seo;
}

function resolveRedirectUrl(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed || typeof window === 'undefined') return null;
  try {
    const url = trimmed.startsWith('/') && !trimmed.startsWith('//')
      ? new URL(trimmed, window.location.origin)
      : new URL(trimmed);
    if (!['http:', 'https:'].includes(url.protocol)) return null;
    return url.href === window.location.href ? null : url.href;
  } catch {
    return null;
  }
}

export function PublicSitePage() {
  const { slug } = useParams<{ slug: string }>();
  const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const platformHostname = (import.meta.env.VITE_PLATFORM_HOSTNAME as string | undefined) ?? 'lumenise.app';
  const isPlatformHost =
    hostname === platformHostname ||
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.endsWith('.pages.dev');

  const { data: resolvedSite, isLoading: resolvingHost } = useQuery({
    queryKey: ['public-site-host', hostname],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('resolve_site_by_hostname', { input_hostname: hostname });
      if (error) throw error;
      return data?.[0] ?? null;
    },
    enabled: !slug && !!hostname && !isPlatformHost,
  });

  const effectiveSlug = slug ?? resolvedSite?.slug;

  const { data: site, isLoading: loadingSite } = useQuery({
    queryKey: ['public-site', effectiveSlug],
    queryFn: async () => {
      if (!effectiveSlug) return null;
      const { data, error } = await supabase
        .from('sites')
        .select('id, name, slug, front_page_mode, home_page_id, theme_settings, site_branding(*)')
        .eq('slug', effectiveSlug)
        .eq('active', true)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!effectiveSlug,
  });

  const staticHome = site?.front_page_mode === 'page' && !!site?.home_page_id;

  const { data: homePage } = useQuery({
    queryKey: ['public-home-page', site?.home_page_id],
    queryFn: async () => {
      if (!site?.home_page_id) return null;
      const nowIso = new Date().toISOString();
      const { data, error } = await supabase
        .from('posts')
        .select('id, title, excerpt, content, template_id, layout, seo, og, redirect_enabled, redirect_url, redirect_status')
        .eq('id', site.home_page_id)
        .or(`status.eq.published,and(status.eq.scheduled,scheduled_at.lte.${nowIso})`)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: staticHome,
    refetchInterval: staticHome ? 60_000 : false,
  });

  const branding = site?.site_branding as PublicBranding | null;
  const appearanceTemplateId = staticHome ? getTemplateIdForContent(branding, homePage?.template_id, 'page') : null;

  const { data: appearanceTemplate } = useQuery({
    queryKey: ['appearance-template', appearanceTemplateId],
    queryFn: async () => {
      if (!appearanceTemplateId) return null;
      const { data, error } = await supabase.from('appearance_templates').select('*').eq('id', appearanceTemplateId).single();
      if (error) throw error;
      return data;
    },
    enabled: !!appearanceTemplateId,
  });

  const { data: posts } = useQuery({
    queryKey: ['public-posts', site?.id],
    queryFn: async () => {
      if (!site?.id) return [];
      const nowIso = new Date().toISOString();
      const { data, error } = await supabase
        .from('posts')
        .select('id, title, slug, excerpt, published_at')
        .eq('site_id', site.id)
        .or(`status.eq.published,and(status.eq.scheduled,scheduled_at.lte.${nowIso})`)
        .not('type_id', 'is', null)
        .order('published_at', { ascending: false })
        .limit(6);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!site?.id && !staticHome,
    refetchInterval: !staticHome ? 60_000 : false,
  });

  const isBlank = staticHome && homePage?.layout === 'blank';

  const { data: menu } = usePublicMenu(isBlank ? undefined : site?.id);

  const siteChromeBranding = site?.site_branding as PublicBranding & { header_template_id?: string | null; footer_template_id?: string | null };
  const { headerTemplate, footerTemplate } = useSiteChrome(
    isBlank ? null : siteChromeBranding?.header_template_id,
    isBlank ? null : siteChromeBranding?.footer_template_id,
    siteChromeBranding,
  );
  const siteName = branding?.app_name ?? site?.name ?? 'Lumenise';
  const siteUrl = typeof window !== 'undefined'
    ? (isPlatformHost ? `${window.location.origin}/s/${site?.slug ?? effectiveSlug}/home` : `${window.location.origin}/`)
    : undefined;
  const seo = getSiteSeo(site?.theme_settings);
  const homeSeo = staticHome ? getContentSeo(homePage?.seo) : {};
  const homeOg = staticHome ? getContentSeo(homePage?.og) : {};
  const browserTitle = homeSeo.meta_title?.trim() || seo.meta_title?.trim() || branding?.browser_title?.trim() || siteName;
  const seoDescription = homeSeo.meta_description?.trim() || homePage?.excerpt?.trim() || seo.meta_description?.trim();
  const socialImageUrl = homeOg.og_image?.trim() || homeSeo.og_image?.trim() || seo.og_image?.trim() || branding?.social_image_url;
  const homeRedirectUrl = staticHome && homePage?.redirect_enabled ? resolveRedirectUrl(homePage.redirect_url) : null;

  const appearance = resolveAppearance(branding, appearanceTemplate);
  const isBoxed = appearance.layout_mode === 'boxed';

  useEffect(() => {
    loadGoogleFont(appearance.heading_font);
    loadGoogleFont(appearance.body_font);
  }, [appearance.heading_font, appearance.body_font]);

  useEffect(() => {
    if (!homeRedirectUrl) return;
    window.location.replace(homeRedirectUrl);
  }, [homeRedirectUrl]);

  if (resolvingHost || loadingSite) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-900" />
      </div>
    );
  }

  if (!effectiveSlug || !site) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white px-6 text-slate-900">
        <div className="max-w-md space-y-3 text-center">
          <AlertCircle className="mx-auto h-8 w-8 text-rose-500" />
          <h1 className="font-display text-2xl font-bold">Site não encontrado</h1>
          <p className="text-sm text-slate-500">Este domínio ainda não está ativo ou não está vinculado a um site publicado.</p>
        </div>
      </div>
    );
  }

  if (homeRedirectUrl) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-900" />
      </div>
    );
  }

  const homeBlocks = (homePage?.content as unknown as Block[]) || [];
  const templateBlocks = appearanceTemplate?.template_mode === 'dynamic'
    ? ((appearanceTemplate.content as unknown as Block[]) || [])
    : null;
  const renderBlocks = templateBlocks ?? homeBlocks;
  const pureHtml = staticHome ? getPureHtmlContent(homePage?.layout, renderBlocks) : null;

  // Home estática "Em branco" com um único bloco HTML: mesmo tratamento de
  // PublicPageView — ver getPureHtmlContent para o motivo (script via
  // innerHTML nunca executa; srcDoc cria um documento de verdade).
  if (pureHtml !== null) {
    return (
      <div className="flex min-h-screen flex-col">
        <CustomScriptsInjector
          headCode={branding?.custom_head_code}
          bodyStartCode={branding?.custom_body_start_code}
          bodyEndCode={branding?.custom_body_end_code}
        />
        <GoogleAnalyticsInjector measurementId={branding?.google_analytics_measurement_id} />
        <PublicViewTracker postId={homePage?.id} enabled={staticHome} />
        <PublicSiteMeta title={browserTitle} description={seoDescription} faviconUrl={branding?.favicon_url} socialImageUrl={socialImageUrl} siteName={siteName} siteUrl={siteUrl} alternateName={site?.name} isHomePage />
        <AdminBar siteId={site.id} postId={homePage?.id ?? null} />
        <iframe
          srcDoc={pureHtml}
          title={homePage?.title || siteName}
          allow="autoplay; fullscreen; picture-in-picture"
          className="block w-full flex-1 border-0"
        />
        <CookieBanner siteKey={site?.id} />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-white text-slate-900"
      style={{
        ...appearanceCssVars(appearance),
        backgroundColor: 'var(--color-page-bg)',
        backgroundImage: 'var(--bg-image-page)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <CustomScriptsInjector
        headCode={branding?.custom_head_code}
        bodyStartCode={branding?.custom_body_start_code}
        bodyEndCode={branding?.custom_body_end_code}
      />
      <GoogleAnalyticsInjector measurementId={branding?.google_analytics_measurement_id} />
      <PublicViewTracker postId={homePage?.id} enabled={staticHome} />
      <PublicSiteMeta title={browserTitle} description={seoDescription} faviconUrl={branding?.favicon_url} socialImageUrl={socialImageUrl} siteName={siteName} siteUrl={siteUrl} alternateName={site?.name} isHomePage />
      <AdminBar siteId={site.id} postId={staticHome ? homePage?.id : null} />

      {!isBlank && (
        <PublicHeader
          siteSlug={site.slug}
          siteName={siteName}
          logoUrl={branding?.logo_url}
          menu={menu ?? []}
          siteId={site.id}
          customBlocks={headerTemplate?.blocks}
          customAppearance={headerTemplate?.appearance}
        />
      )}

      <main
        className={isBoxed ? 'mx-auto w-full overflow-hidden shadow-xl' : undefined}
        style={{
          padding: 'var(--page-padding)',
          marginTop: 'var(--page-margin)',
          marginBottom: 'var(--page-margin)',
          ...(isBoxed
            ? {
                maxWidth: 'min(var(--box-width), 100%)',
                borderRadius: 'var(--box-radius)',
                backgroundColor: 'var(--color-box-bg)',
                backgroundImage: 'var(--bg-image-box)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : undefined),
        }}
      >
        {staticHome ? (
          <PublicBlockRenderer blocks={renderBlocks} siteId={site.id} siteSlug={site.slug} fallbackImageAlt={homePage?.title || siteName} />
        ) : (
          <section className="mx-auto max-w-6xl px-6 py-24">
            <div className="mx-auto mb-4 h-1 w-14 rounded-full" style={{ background: 'var(--color-accent)' }} />
            <h1 className="text-center text-4xl font-extrabold tracking-tight" style={{ color: 'var(--color-heading, #0f172a)', fontFamily: 'var(--font-heading, var(--font-display))' }}>Publicações</h1>
          </section>
        )}

        {!staticHome && posts && posts.length > 0 && (
          <section
            className="w-full border-t py-20 md:py-28"
            style={{
              backgroundColor: 'color-mix(in oklab, var(--color-page-bg), var(--color-primary) 4%)',
              borderColor: 'color-mix(in oklab, var(--color-primary), transparent 90%)',
            }}
          >
            <div className="mx-auto max-w-6xl px-6">
              <div className="mb-12 text-center">
                <div className="mx-auto mb-5 h-1 w-14 rounded-full" style={{ background: 'var(--color-accent)' }} />
                <h2 className="text-3xl font-extrabold tracking-tight md:text-4xl" style={{ color: 'var(--color-heading, #0f172a)', fontFamily: 'var(--font-heading, var(--font-display))' }}>
                  Últimas publicações
                </h2>
              </div>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                {posts.map((post) => (
                  <Link
                    key={post.id}
                    to={`/s/${site.slug}/${post.slug}`}
                    className="flex flex-col rounded-2xl border p-7 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl"
                    style={{
                      backgroundColor: 'var(--color-box-bg, #ffffff)',
                      borderColor: 'color-mix(in oklab, var(--color-primary), transparent 86%)',
                    }}
                  >
                    {post.published_at && (
                      <span className="text-xs font-medium uppercase tracking-widest" style={{ color: 'var(--color-accent)' }}>
                        {new Date(post.published_at).toLocaleDateString('pt-BR')}
                      </span>
                    )}
                    <h3 className="mt-3 text-xl font-bold" style={{ color: 'var(--color-heading, #0f172a)', fontFamily: 'var(--font-heading, var(--font-display))' }}>{post.title}</h3>
                    {post.excerpt && <p className="mt-2 line-clamp-3 text-sm leading-relaxed" style={{ color: 'var(--color-paragraph, #475569)', fontFamily: 'var(--font-paragraph, var(--font-body))' }}>{post.excerpt}</p>}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      {!isBlank && (
        <PublicFooter
          siteName={siteName}
          siteId={site.id}
          siteSlug={site.slug}
          customBlocks={footerTemplate?.blocks}
          customAppearance={footerTemplate?.appearance}
        />
      )}
      <CookieBanner siteKey={site?.id} />
    </div>
  );
}
